<template>
  <div>
    <router-link :to="{ name: 'contentManagement' }" class="return-link">Return to Content Management</router-link>

    <div class="separator bottom"></div>
    <div class="innerLR">
      <b-col md="12">
        <b-form class="form-horizontal" @submit.prevent="submitContent">
          <b-form-group label="Type" label-class="control-label" :class="{ 'invalid-group': $v.form.contentTypeId.$error }">
            <b-form-select :class="{ 'is-invalid': $v.form.contentTypeId.$error }"
                           :options="contentTypes"
                           value-field="id"
                           text-field="name"
                           v-model="form.contentTypeId"
                           @change="checkCategoryValidity"></b-form-select>
            <span class="span-feedback invalid-feedback" v-if="$v.form.contentTypeId.$error">You must select a content type.</span>
          </b-form-group>

          <b-form-group label="Category" label-class="control-label" :class="{ 'invalid-group': $v.form.contentCategoryId.$error }">
            <b-form-select :class="{ 'is-invalid': $v.form.contentCategoryId.$error }"
                           :options="contentCategories"
                           value-field="id"
                           text-field="name"
                           v-model="form.contentCategoryId"
                           :disabled="!selectedType"></b-form-select>
            <span class="span-feedback invalid-feedback" v-if="$v.form.contentCategoryId.$error">You must select a content category.</span>
          </b-form-group>

          <b-form-group label="Title" label-class="control-label" :class="{ 'invalid-group': $v.form.title.$error }">
            <b-form-input v-model="form.title" :class="{ 'is-invalid': $v.form.title.$error }"></b-form-input>
            <span class="span-feedback invalid-feedback" v-if="$v.form.title.$error">You must enter a title.</span>
          </b-form-group>

          <div class="academic-plan-div">
            <span class="col-form-label pt-0 control-label">Academic Plans</span>
            <b-form-checkbox v-model="allChsPlans" class="chs-checkbox">Viewable by all CHS students</b-form-checkbox>
            <autocomplete :source="unselectedAcademicPlans"
                          :results-display="academicPlanDisplay"
                          placeholder="Search to add plans"
                          @selected="addAcademicPlan"
                          :disable-input="allChsPlans"
                          :disabled="allChsPlans"
                          :class = "{ 'disabled-autoselect': allChsPlans }"
                          :inputClass="{ 'form-control': true,
                                          'text-input': true}"/>
            <div v-if="!allChsPlans">
              <div v-for="contentAcademicPlan in form.contentAcademicPlans" :key="contentAcademicPlan.academicPlanId">
                <b-badge @click="removeAcademicPlan(contentAcademicPlan.academicPlanId)" class="plan-badge">
                    {{ contentAcademicPlan.academicPlan.description }} <font-awesome-icon icon="times" />
                </b-badge>
              </div>
            </div>
          </div>
          
          <div class="tags-div">
            <span class="col-form-label pt-0 control-label">Tags</span>
            <vue-tags-input v-model="tag"
                            :tags="selectedTagNames"
                            :autocomplete-items="filteredTags"
                            @tags-changed="updateSelectedTags"
                            class="tag-editor" />
          </div>

          <b-form-group label="Description" label-class="control-label" :class="{ 'invalid-group': $v.decodedDescription.$error }">
            <vue-editor v-model="decodedDescription" :class="{ 'is-invalid': $v.decodedDescription.$error }" id="description-editor"></vue-editor>
            <span class="span-feedback invalid-feedback" v-if="$v.decodedDescription.$error">You must enter a description.</span>
          </b-form-group>

          <b-form-group label="Preview (limit 500 characters)"
                        label-class="control-label"
                        :class="{ 'invalid-group': $v.decodedPreview.$error || $v.previewPlaintext.$error }">
            <vue-editor v-model="decodedPreview" :class="{ 'is-invalid': $v.decodedPreview.$error }" id="preview-editor"></vue-editor>
            <span class="span-feedback invalid-feedback inline-span" v-if="$v.decodedPreview.$error">You must enter preview text.</span>
            <span class="span-feedback invalid-feedback inline-span" v-if="$v.previewPlaintext.$error">Preview text cannot exceed 500 characters.</span>
            <span :class="{ 'char-counter': true, 'is-invalid': $v.previewPlaintext.$invalid }">{{ previewPlaintext.length }} / 500</span>
          </b-form-group>
        </b-form>
      </b-col>

      <b-button class="pull-right content-button" variant="primary" @click="saveContent">Save</b-button>
      <b-button class="pull-right content-button" :to="{ path: '/content/management' }">Cancel</b-button>
    </div>
  </div>
</template>

<script>
// text editor
import { VueEditor } from "vue2-editor";

import { required, maxLength } from "vuelidate/lib/validators";
import * as _ from "lodash"; // eslint-disable-line no-unused-vars
import VueTagsInput from "@johmun/vue-tags-input";

var htmlEncode = require("js-htmlencode").htmlEncode;
var htmlDecode = require("js-htmlencode").htmlDecode;

export default {
  data() {
    return {
      form: {
        contentTypeId: null,
        contentCategoryId: null,
        name: null,
        title: null,
        description: null,
        previous: null,
        showInMenu: false,
        contentAcademicPlans: [],
        contentTags: []
      },
      decodedDescription: null,
      decodedPreview: null,
      selectedAcademicPlans: [],
      allChsPlans: false,
      tag: "",
      selectedTagNames: []
    };
  },

  props: {
    contentTypes: {
      type: Array,
      required: true
    },

    academicPlans: {
      type: Array,
      required: true
    },

    tags: {
      type: Array,
      required: true
    },

    content: {
      type: Object,
      required: false
    }
  },

  validations: {
    form: {
      contentTypeId: {
        required
      },
      contentCategoryId: {
        required
      },
      title: {
        required
      }
    },

    decodedDescription: {
      required
    },

    decodedPreview: {
      required
    },

    previewPlaintext: {
      maxLength: maxLength(500)
    }
  },

  computed: {
    selectedType: function() {
      var that = this;
      if (this.form.contentTypeId) {
        return _.find(this.contentTypes, function(contentType) {
          return contentType.id === that.form.contentTypeId;
        });
      }
      return null;
    },

    contentCategories: function() {
      var categories = [];
      if (this.selectedType) {
        _.forEach(this.selectedType.contentTypeCategories, function(
          contentTypeCategory
        ) {
          categories.push(contentTypeCategory.contentCategory);
        });
      }
      return categories;
    },

    unselectedAcademicPlans: function() {
      var that = this;
      var unselectedAcademicPlans = [];
      _.forEach(this.academicPlans, function(academicPlan) {
        var alreadySelected = _.find(that.form.contentAcademicPlans, function(
          contentAcademicPlan
        ) {
          return (
            academicPlan.academicPlanId === contentAcademicPlan.academicPlanId
          );
        });
        if (!alreadySelected) {
          unselectedAcademicPlans.push(academicPlan);
        }
      });
      return unselectedAcademicPlans;
    },

    autocompleteTags: function() {
      var autocompleteItems = this.tags.map(t => {
        return { text: t.name };
      });
      return autocompleteItems;
    },

    filteredTags: function() {
      return this.autocompleteTags.filter(i =>
        new RegExp(this.tag).test(i.text)
      );
    },

    previewPlaintext: function() {
      return this.getPlaintext(this.decodedPreview);
    }
  },

  components: {
    VueEditor,
    VueTagsInput
  },

  methods: {
    // if a type is selected and the category is no longer an option, set contentCategoryId to null
    checkCategoryValidity: function(contentTypeId) {
      var that = this;
      var currentContentType = _.find(this.contentTypes, function(contentType) {
        return contentType.id === contentTypeId;
      });
      var foundMatch = _.find(
        currentContentType.contentTypeCategories,
        function(contentTypeCategory) {
          return (
            contentTypeCategory.contentCategoryId ===
            that.form.contentCategoryId
          );
        }
      );
      if (!foundMatch) {
        this.form.contentCategoryId = null;
      }
    },

    calculateNameFromTitle: function(title) {
      if (title) {
        return title
          .toLowerCase()
          .split(" ")
          .join("-");
      } else {
        return "";
      }
    },

    encodeText: function(text) {
      if (text) {
        return htmlEncode(text);
      }
      return "";
    },

    academicPlanDisplay: function(academicPlan) {
      return academicPlan.description + " - " + academicPlan.planCode;
    },

    addAcademicPlan: function(autoselectObject) {
      var contentAcademicPlan = {
        academicPlanId: autoselectObject.selectedObject.academicPlanId,
        academicPlan: autoselectObject.selectedObject
      };
      this.form.contentAcademicPlans.push(contentAcademicPlan);
    },

    removeAcademicPlan: function(academicPlanId) {
      this.form.contentAcademicPlans = _.reject(
        this.form.contentAcademicPlans,
        function(contentAcademicPlan) {
          return contentAcademicPlan.academicPlanId === academicPlanId;
        }
      );
    },

    getPlaintext: function(html) {
      var div = document.createElement("div");
      div.innerHTML = html;
      return div.textContent || div.innerText || "";
    },

    addAllChsPlans: function() {
      var that = this;
      _.forEach(this.unselectedAcademicPlans, function(academicPlan) {
        var contentAcademicPlan = {
          academicPlanId: academicPlan.academicPlanId,
          academicPlan: academicPlan
        };
        that.form.contentAcademicPlans.push(contentAcademicPlan);
      });
    },

    updateSelectedTags: function(value) {
      var that = this;
      var contentTags = [];
      _.forEach(value, function(tagNameObject) {
        var tag = _.find(that.tags, function(actualTag) {
          return actualTag.name === tagNameObject.text;
        });
        var contentTag = {
          tag: tag
        };
        if (tag) {
          contentTag.tagId = tag.id;
        } else {
          var newTag = { name: tagNameObject.text };
          contentTag.tag = newTag;
          contentTag.tagId = 0;
        }
        contentTags.push(contentTag);
      });
      this.form.contentTags = contentTags;
    },

    saveContent: function() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        // calculate name and add to form
        this.form.name = this.calculateNameFromTitle(this.form.title);
        // encode description and previous
        this.form.description = htmlEncode(this.decodedDescription);
        this.form.preview = htmlEncode(this.decodedPreview);

        if (this.allChsPlans) {
          this.addAllChsPlans();
        }

        // emit completed form
        this.$emit("save-content", this.form);
      }
    }
  },

  created() {
    if (this.content) {
      this.form.id = this.content.id;
      this.form.contentTypeId = this.content.contentTypeId;
      this.form.contentCategoryId = this.content.contentCategoryId;
      this.form.name = this.content.name;
      this.form.title = this.content.title;
      this.form.description = this.content.description;
      this.decodedDescription = htmlDecode(this.content.description);
      this.form.preview = this.content.preview;
      this.decodedPreview = htmlDecode(this.content.preview);
      this.form.showInMenu = this.content.showInMenu;

      this.form.contentAcademicPlans = this.content.contentAcademicPlans;
      if (this.form.contentAcademicPlans.length === this.academicPlans.length) {
        this.allChsPlans = true;
      }

      this.form.contentTags = this.content.contentTags;
      this.selectedTagNames = _.map(this.content.contentTags, function(
        contentTag
      ) {
        return { text: contentTag.tag.name };
      });
    }
  }
};
</script>

<style>
.control-label {
  padding-bottom: 0;
}

.autocomplete__results {
  max-height: 300px;
}

.plan-badge {
  cursor: pointer;
}

.academic-plan-div,
.tags-div {
  margin-bottom: 10px;
}

.tags-div .tag-editor.vue-tags-input {
  width: 100%;
  max-width: 100%;
}

.tag-editor.vue-tags-input .input {
  width: 100%;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.tag-editor.vue-tags-input .input:focus-within {
  /* border-color: #80bdff; */
  /*box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);*/
}

.tag.valid {
  background-color: #e04545 !important;
}

.chs-checkbox {
  margin-left: 40px;
}

.disabled-autoselect .autocomplete__box {
  background-color: #e9ecef;
}

input[type="text"]:disabled {
  background-color: #e9ecef;
}

.content-button {
  margin: 0 5px 0 5px;
}

.span-feedback.inline-span {
  display: inline;
}

.char-counter {
  float: right;
  display: inline;
  font-size: 14px;
}

.char-counter.is-invalid {
  color: #dc3545;
}
</style>
